import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

import Modal from 'components/common/Modal';
import { createBugTask, uploadReportBugScreenshot } from 'api/reportBug.api';
import { API_URL } from 'constants/env';
import {
  DOMAIN_OPTIONS,
  ISSUE_TYPE_OPTIONS,
  PRIORITY_OPTIONS,
  SUBMIT_TICKET,
  TEAM_OPTIONS,
} from 'constants/reportBugConstants';
import { AuthManager } from 'config/auth.config';
import { logger } from 'utils/logger';

interface SubmitTicketModalProps {
  open: boolean;
  onClose: () => void;
  /** Pre-captured screenshot data URL (PNG). Optional. */
  screenshotDataUrl?: string | null;
}

interface FormState {
  title: string;
  team: string;
  domain: string;
  type: string;
  priority: string;
  description: string;
}

const HTTP_CREATED = 201;

/*
  Success banner stays visible briefly so the user gets feedback before the
  modal disappears.
*/
const SUCCESS_BANNER_VISIBLE_MS = 1500;

/*
  The submit-ticket modal pierces the shared <Modal> wrapper to recolour the
  panel with the dark "Report an Issue" palette and to paint a lime accent
  line under the modal title. The Modal component renders `.rw-modal__*`
  itself, so the only way to restyle those internals without editing Modal
  is a scoped <style> block carrying the same CSS as the original module.
  Tokens originally lived as `--st-*` CSS variables; here they're inlined.
*/
const SUBMIT_TICKET_MODAL_STYLE = `
.submit-ticket-modal .rw-modal__backdrop { background: rgba(0, 0, 0, 0.72); }
.submit-ticket-modal .rw-modal__panel {
  background: #1a1a1a;
  color: #ffffff;
  border: 1px solid #2a2a2a;
  border-radius: 10px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55);
}
.submit-ticket-modal .rw-modal__header {
  border-bottom: 1px solid #2a2a2a;
  padding: 18px 22px 14px;
  position: relative;
}
.submit-ticket-modal .rw-modal__title {
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.2px;
}
.submit-ticket-modal .rw-modal__title::after {
  content: '';
  position: absolute;
  left: 22px;
  bottom: -1px;
  width: 110px;
  height: 2px;
  background: #0eaa6b;
  border-radius: 1px;
}
.submit-ticket-modal .rw-modal__close { color: rgba(255, 255, 255, 0.62); }
.submit-ticket-modal .rw-modal__close:hover {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.06);
}
.submit-ticket-modal .rw-modal__body { padding: 20px 22px 8px; }
.submit-ticket-modal .rw-modal__footer {
  border-top: 1px solid #2a2a2a;
  padding: 14px 22px;
  gap: 12px;
}
@media (max-width: 640px) {
  .submit-ticket-modal .rw-modal__body,
  .submit-ticket-modal .rw-modal__header,
  .submit-ticket-modal .rw-modal__footer {
    padding-left: 16px;
    padding-right: 16px;
  }
  .submit-ticket-modal .rw-modal__title::after { left: 16px; }
}
`;

// ── Tailwind class bundles ────────────────────────────────────────────────
const FORM_CLASS = 'flex flex-col gap-4';

const FIELD_CLASS = 'flex flex-col gap-[6px] flex-1 min-w-0';

// Row that holds two side-by-side fields. Collapses to a single column
// under 640 px.
const ROW_CLASS =
  'flex gap-4 max-[640px]:flex-col max-[640px]:gap-[14px]' +
  ' [&>.ticket-form__field]:flex-1';

const LABEL_CLASS =
  'text-xs font-hk-medium text-white/[0.62] tracking-[0.1px]';

// Shared field shell — input / select / textarea. Hover lights the border
// green with reduced opacity; focus locks the accent border with a soft
// inner ring and lifts the background a tone.
const FIELD_SHELL_BASE =
  'w-full py-2 px-3 rounded-md border border-solid border-[#373737]' +
  ' bg-[#252525] text-white text-[13px] font-[inherit] outline-0' +
  ' transition-[border-color,box-shadow,background-color] duration-[150ms] ease-linear' +
  ' placeholder:text-white/[0.45]' +
  ' focus:border-[#0eaa6b] focus:shadow-[0_0_0_2px_rgba(14,170,107,0.22)] focus:bg-[#1e1e1e]' +
  ' disabled:opacity-55 disabled:cursor-not-allowed';

const INPUT_CLASS =
  FIELD_SHELL_BASE +
  ' h-[38px] enabled:[&:not(.ticket-form__input--error):not(:disabled)]:hover:border-[rgba(14,170,107,0.55)]';

const INPUT_ERROR_EXTRA =
  ' !border-[#f56c6c] focus:!shadow-[0_0_0_2px_rgba(245,108,108,0.22)]';

// Select shell — adds the native chevron replacement via a data: URL
// background image so the select chevron follows the dark theme.
const SELECT_CHEVRON_BG =
  "url(\"data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23ffffff' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round' opacity='0.55'%3E%3Cpath d='M5 9l7 7 7-7'/%3E%3C/svg%3E\")";

const SELECT_CLASS =
  FIELD_SHELL_BASE +
  ' h-[38px] appearance-none pr-8 bg-no-repeat [background-position:right_10px_center] [background-size:12px_12px]' +
  ' enabled:hover:border-[rgba(14,170,107,0.55)]';
const SELECT_STYLE: React.CSSProperties = {
  backgroundImage: SELECT_CHEVRON_BG,
};

const TEXTAREA_CLASS =
  FIELD_SHELL_BASE +
  ' h-auto min-h-[84px] py-[10px] px-3 leading-[1.5] resize-y' +
  ' enabled:hover:border-[rgba(14,170,107,0.55)]';

const ERROR_TEXT_CLASS = 'text-[#f56c6c] text-xs';

// Alert/banner — danger by default, success variant via the `--success` class
// in the original CSS, mapped to `ALERT_SUCCESS_EXTRA` below.
const ALERT_BASE =
  'py-[10px] px-3 rounded-md text-[13px]' +
  ' bg-[rgba(245,108,108,0.08)] text-[#f56c6c]' +
  ' border border-solid border-[rgba(245,108,108,0.45)]';

const ALERT_SUCCESS_EXTRA =
  ' !bg-[rgba(14,170,107,0.1)] !text-[var(--color-accent)] !border-[rgba(14,170,107,0.5)]';

const SCREENSHOT_CLASS =
  'block max-w-full max-h-[220px] border border-solid border-[#373737] rounded-md object-contain bg-[#0f0f0f]';

// Footer buttons — secondary (outlined) and primary (green) with the active
// translate-y press effect on the primary variant.
const BTN_BASE =
  'h-9 px-[22px] rounded-md text-[13px] font-hk-semibold tracking-[0.2px] cursor-pointer' +
  ' border border-solid border-transparent' +
  ' transition-[background-color,color,border-color,box-shadow] duration-[150ms] ease-linear' +
  ' disabled:opacity-55 disabled:cursor-not-allowed' +
  ' focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_rgba(14,170,107,0.4)]';
const BTN_SECONDARY =
  ' bg-transparent border-[#373737] text-white' +
  ' enabled:hover:bg-white/[0.06] enabled:hover:border-white/[0.25]';
const BTN_PRIMARY =
  ' bg-[#0eaa6b] border-[#0eaa6b] text-white' +
  ' enabled:hover:bg-[#0c8d59] enabled:hover:border-[#0c8d59]' +
  ' enabled:active:translate-y-px';

const isProductionServer = (): boolean =>
  typeof API_URL === 'string' && API_URL.includes('rwprod');

const buildInitialForm = (): FormState => ({
  title: '',
  team: SUBMIT_TICKET.DEFAULT_TEAM,
  domain: '',
  type: SUBMIT_TICKET.DEFAULT_ISSUE_TYPE,
  priority: SUBMIT_TICKET.DEFAULT_TASK_PRIORITY,
  description: '',
});

const SubmitTicketModal: React.FC<SubmitTicketModalProps> = ({
  open,
  onClose,
  screenshotDataUrl = null,
}) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormState>(buildInitialForm);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const titleInputRef = useRef<HTMLInputElement | null>(null);
  const closeTimerRef = useRef<number | null>(null);

  const clearCloseTimer = useCallback((): void => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!open) {
      setForm(buildInitialForm());
      setErrorMessage(null);
      setSuccessMessage(null);
      setSubmitting(false);
      clearCloseTimer();
    }
  }, [open, clearCloseTimer]);

  useEffect(() => clearCloseTimer, [clearCloseTimer]);

  const titleMissing = form.title.trim().length === 0;

  const handleField = useCallback(
    <K extends keyof FormState>(key: K) =>
      (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
        setForm((prev) => ({ ...prev, [key]: event.target.value }));
      },
    [],
  );

  const projectId = useMemo(
    () => (isProductionServer() ? SUBMIT_TICKET.PROD_PROJECT_ID : SUBMIT_TICKET.QA_PROJECT_ID),
    [],
  );

  const handleSubmit = useCallback(async (): Promise<void> => {
    if (titleMissing || submitting) return;
    const reporterEmail = AuthManager.getUser()?.email ?? '';
    setSubmitting(true);
    setErrorMessage(null);

    const reporterLine = reporterEmail ? `\n\nReported by: ${reporterEmail}` : '';

    try {
      // Vue parity: the screenshot is uploaded to S3 first so the ticket
      // description can link to the actual image, rather than embedding a
      // data URL (or, as before this fix, a placeholder line describing its
      // size with nothing attached).
      let screenshotLine = '';
      if (screenshotDataUrl) {
        const screenshotUrl = await uploadReportBugScreenshot(screenshotDataUrl);
        screenshotLine = `\n\n${SUBMIT_TICKET.SCREENSHOT_LINK_LABEL}: ${screenshotUrl}`;
      }

      const result = await createBugTask({
        title: form.title.trim(),
        projects: [projectId],
        description: `${form.description}${screenshotLine}${reporterLine}`,
        customFields: {
          [SUBMIT_TICKET.CUSTOM_FIELD_PRIORITY]: form.priority,
          [SUBMIT_TICKET.CUSTOM_FIELD_TYPE]: form.type,
          [SUBMIT_TICKET.CUSTOM_FIELD_DOMAIN]: form.domain || null,
          [SUBMIT_TICKET.CUSTOM_FIELD_TEAM]: form.team,
        },
      });

      if (result.status === HTTP_CREATED) {
        setSuccessMessage(t('reportBug.successMessage'));
        clearCloseTimer();
        closeTimerRef.current = window.setTimeout(() => {
          closeTimerRef.current = null;
          onClose();
        }, SUCCESS_BANNER_VISIBLE_MS);
      } else {
        setErrorMessage(t('reportBug.failureMessage'));
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'unknown';
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      logger.error('SubmitTicketModal: ticket submission failed', { message, status });
      setErrorMessage(t('reportBug.failureMessage'));
    } finally {
      setSubmitting(false);
    }
  }, [
    form,
    projectId,
    screenshotDataUrl,
    titleMissing,
    submitting,
    onClose,
    t,
    clearCloseTimer,
  ]);

  const isFinalizing = successMessage !== null;

  return (
    <>
      <style>{SUBMIT_TICKET_MODAL_STYLE}</style>
      <Modal
        open={open}
        onClose={submitting || isFinalizing ? () => undefined : onClose}
        title={t('reportBug.heading')}
        size="lg"
        className="submit-ticket-modal"
        footer={
          <>
            <button
              type="button"
              className={BTN_BASE + BTN_SECONDARY}
              onClick={() => {
                clearCloseTimer();
                onClose();
              }}
              disabled={submitting}
            >
              {t('reportBug.cancel')}
            </button>
            <button
              type="button"
              className={BTN_BASE + BTN_PRIMARY}
              onClick={handleSubmit}
              disabled={submitting || titleMissing || isFinalizing}
            >
              {submitting ? t('reportBug.submitting') : t('reportBug.submit')}
            </button>
          </>
        }
      >
        <form
          className={FORM_CLASS}
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit();
          }}
        >
          {screenshotDataUrl ? (
            <div className={FIELD_CLASS}>
              <label className={LABEL_CLASS}>
                {t('reportBug.capturedScreenshot')}
              </label>
              <img
                src={screenshotDataUrl}
                alt={t('reportBug.capturedScreenshot')}
                className={SCREENSHOT_CLASS}
              />
            </div>
          ) : null}

          <div className={FIELD_CLASS}>
            <label htmlFor="rb-title" className={LABEL_CLASS}>
              {t('reportBug.title')}
            </label>
            <input
              ref={titleInputRef}
              id="rb-title"
              type="text"
              className={
                INPUT_CLASS + (titleMissing ? INPUT_ERROR_EXTRA : '')
              }
              placeholder={t('reportBug.titlePlaceholder')}
              value={form.title}
              onChange={handleField('title')}
              disabled={submitting}
              aria-required="true"
              aria-invalid={titleMissing}
            />
            {titleMissing ? (
              <small className={ERROR_TEXT_CLASS}>
                {t('reportBug.titleRequired')}
              </small>
            ) : null}
          </div>

          <div className={ROW_CLASS}>
            <div className={`ticket-form__field ${FIELD_CLASS}`}>
              <label htmlFor="rb-team" className={LABEL_CLASS}>
                {t('reportBug.team')}
              </label>
              <select
                id="rb-team"
                className={SELECT_CLASS}
                style={SELECT_STYLE}
                value={form.team}
                onChange={handleField('team')}
                disabled={submitting}
              >
                {TEAM_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className={`ticket-form__field ${FIELD_CLASS}`}>
              <label htmlFor="rb-domain" className={LABEL_CLASS}>
                {t('reportBug.domain')}
              </label>
              <select
                id="rb-domain"
                className={SELECT_CLASS}
                style={SELECT_STYLE}
                value={form.domain}
                onChange={handleField('domain')}
                disabled={submitting}
              >
                <option value="">{t('reportBug.selectDomain')}</option>
                {DOMAIN_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className={ROW_CLASS}>
            <div className={`ticket-form__field ${FIELD_CLASS}`}>
              <label htmlFor="rb-type" className={LABEL_CLASS}>
                {t('reportBug.issueType')}
              </label>
              <select
                id="rb-type"
                className={SELECT_CLASS}
                style={SELECT_STYLE}
                value={form.type}
                onChange={handleField('type')}
                disabled={submitting}
              >
                {ISSUE_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className={`ticket-form__field ${FIELD_CLASS}`}>
              <label htmlFor="rb-priority" className={LABEL_CLASS}>
                {t('reportBug.issuePriority')}
              </label>
              <select
                id="rb-priority"
                className={SELECT_CLASS}
                style={SELECT_STYLE}
                value={form.priority}
                onChange={handleField('priority')}
                disabled={submitting}
              >
                {PRIORITY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className={FIELD_CLASS}>
            <label htmlFor="rb-description" className={LABEL_CLASS}>
              {t('reportBug.issueDescription')}
            </label>
            <textarea
              id="rb-description"
              className={TEXTAREA_CLASS}
              rows={3}
              placeholder={t('reportBug.descriptionPlaceholder')}
              value={form.description}
              onChange={handleField('description')}
              disabled={submitting}
            />
          </div>

          {successMessage ? (
            <div
              className={ALERT_BASE + ALERT_SUCCESS_EXTRA}
              aria-live="polite"
            >
              {successMessage}
            </div>
          ) : null}
          {errorMessage ? (
            <div className={ALERT_BASE} role="alert">
              {errorMessage}
            </div>
          ) : null}
        </form>
      </Modal>
    </>
  );
};

export default SubmitTicketModal;
